/**
 * Option catalogs for the camera slot chips.
 *
 * Movement borrows the director preset catalog, which is the vocabulary the
 * compiler already emits. Framing has no preset catalog of its own -- the
 * director row never offered one -- so it lists the framings the shot tag
 * already uses, rather than inventing a new vocabulary for the chip.
 */
import { CAMERA_PRESETS } from './cameraPresets';
import { buildBlockingOptions, DEVICE_OPTIONS } from './directionSlotOptions';
// The marker vocabulary, not a second copy of it. `shotPromptMarkers` imports nothing, so
// reading the role grammar from its owner here costs no cycle -- and restating the regex
// beside `CAST_PERFORMANCE_SLOT` below is precisely the drift this catalog has paid for
// before, when the read path and the write path each kept their own idea of the chip list.
import { getCastPerformanceSlotRole } from '../../services/shots/shotPromptMarkers';

export const CAMERA_FRAMING_OPTIONS = Object.freeze([
  'Extreme Close-Up',
  'Close-Up',
  'Medium Close-Up',
  'Medium Shot',
  'Medium Wide',
  'Wide Shot',
  'Extreme Wide',
  'Over the Shoulder',
  'Point of View',
  'Two Shot',
  'Insert',
].map((label) => Object.freeze({ id: label, label, description: null })));

export const CAMERA_MOVEMENT_OPTIONS = Object.freeze(
  CAMERA_PRESETS.map((preset) => Object.freeze({
    id: preset.promptText,
    label: preset.label,
    description: preset.promptText,
  })),
);

export const CAMERA_LENS_OPTIONS = Object.freeze([
  '24mm',
  '35mm',
  '50mm',
  '85mm portrait',
  '100mm macro',
  'anamorphic',
].map((label) => Object.freeze({ id: label, label, description: null })));

/**
 * Per-shot durations, as the planner already constrains them: integer seconds
 * 1..15 (`videoShotPlannerConfig.js:33`). The MODEL's own duration menu bounds
 * the dispatched TOTAL, not each shot -- 8s across 7 shots is 56s and no model
 * accepts that -- so the per-shot options are the planner's rule, and the sum is
 * gated at dispatch.
 */
export const SHOT_DURATION_OPTIONS = Object.freeze(
  Array.from({ length: 15 }, (_, i) => {
    const seconds = i + 1;
    return Object.freeze({
      id: String(seconds),
      label: `${seconds}s`,
      description: null,
    });
  }),
);

/**
 * The slots this surface can steer, and what to call them when unset. Adding a
 * row here is the only thing needed to offer a new chip.
 *
 * `coerce` exists because chip option ids are strings while some slots are
 * typed. `mediaTarget.duration` is one: `normalizeMediaTarget` keeps it only
 * when `Number.isFinite` passes (`ShotManifest.js:111`), so writing the raw
 * string `"4"` would be silently dropped to null. A generic hook rather than a
 * duration special-case in the write handler.
 */
export const PROMPT_SLOT_CHIPS = Object.freeze({
  // Duration is FIRST, and the order of this literal is the order chips render:
  // `PROMPT_SLOT_CHIP_PATHS` below is `Object.keys` of it. Per James (2026-08-05)
  // duration leads the row because it is the one slot available on every sequence
  // shot from the moment it exists -- the camera slots wait for Enhance. Keeping
  // the order here rather than sorting at a call site means one declaration owns
  // it on every surface.
  'mediaTarget.duration': Object.freeze({
    title: 'Duration',
    placeholder: '+ duration',
    // The ONLY slot that renders while unset. Per James (2026-08-22) a blank chip
    // is clutter he never wants to see, with one exception: a shot with no way to
    // set its length is broken, so duration must be reachable before Enhance runs.
    // Every other slot arrives filled -- from Enhance, or from the `+` control --
    // and a slot that has no value simply is not offered.
    //
    // Declared HERE because the catalog comment above already promises that adding
    // a row is the only thing needed to offer a new chip. A gate kept anywhere else
    // breaks that promise, and it did: three surfaces each carried their own copy
    // of this rule and the composer and storyboard drifted into different blanks.
    showWhenUnset: true,
    // A static list only as the fallback: in sequence mode the host supplies a
    // bounded list, because a shot's duration is a share of a fixed sequence
    // total and options that do not fit must not be offered.
    options: SHOT_DURATION_OPTIONS,
    coerce: (value) => {
      const seconds = Number.parseInt(value, 10);
      return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
    },
    // The manifest stores a number; the chip renders and matches on strings.
    format: (value) => (Number.isFinite(value) ? String(value) : null),
  }),
  'camera.framing': Object.freeze({
    title: 'Framing',
    placeholder: '+ framing',
    options: CAMERA_FRAMING_OPTIONS,
  }),
  'camera.movement': Object.freeze({
    title: 'Camera move',
    placeholder: '+ camera move',
    options: CAMERA_MOVEMENT_OPTIONS,
  }),
});

/** Apply a slot's `coerce` if it declares one. Identity otherwise. */
export function coercePromptSlotChipValue(slotPath, value) {
  const config = PROMPT_SLOT_CHIPS[slotPath];
  return typeof config?.coerce === 'function' ? config.coerce(value) : value;
}

/**
 * Does this slot render a chip while it holds no value?
 *
 * Read through an accessor rather than off the catalog literal, for the same
 * reason `coerce` and `format` are: the catalog stays the one owner of the rule,
 * and a slot the catalog has never heard of answers `false` rather than throwing.
 */
export function promptSlotChipShowsWhenUnset(slotPath) {
  return PROMPT_SLOT_CHIPS[slotPath]?.showWhenUnset === true;
}

/** Render a stored slot value as the string the chip UI matches against. */
export function formatPromptSlotChipValue(slotPath, value) {
  const config = PROMPT_SLOT_CHIPS[slotPath];
  return typeof config?.format === 'function' ? config.format(value) : value;
}

export const PROMPT_SLOT_CHIP_PATHS = Object.freeze(Object.keys(PROMPT_SLOT_CHIPS));

export const ANCHOR_SLOT_CHIPS = Object.freeze({
  'camera.lens': Object.freeze({
    title: 'Lens',
    placeholder: '+ lens',
    options: CAMERA_LENS_OPTIONS,
  }),
  'direction.blocking': Object.freeze({
    title: 'Blocking',
    placeholder: '+ blocking',
    options: buildBlockingOptions([]),
  }),
  'direction.device': Object.freeze({
    title: 'Device',
    placeholder: '+ device',
    options: DEVICE_OPTIONS,
  }),
});

export function derivePhrasableDirectionSlotPaths(...catalogs) {
  return catalogs.flatMap((catalog) => Object.entries(catalog || {})
    .filter(([, config]) => typeof config?.coerce !== 'function')
    .map(([slotPath]) => slotPath));
}

/** Direction facts that can land verbatim in prose instead of a typed store. */
export const PHRASABLE_DIRECTION_SLOT_PATHS = Object.freeze(
  derivePhrasableDirectionSlotPaths(PROMPT_SLOT_CHIPS, ANCHOR_SLOT_CHIPS),
);

const CAST_PERFORMANCE_SLOT = /^cast\[(\d+)\]\.performance$/;
const PERFORMANCE_SLOT_CHIP = Object.freeze({
  title: 'Performance',
  placeholder: '+ performance',
  options: null,
});

export function getCastPerformanceSlotIndex(slotPath) {
  const match = CAST_PERFORMANCE_SLOT.exec(slotPath);
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * The slots a SINGLE clip can steer, in render order.
 *
 * A single clip stores no `ShotManifest`. Two flat lanes own these facts:
 * `videoParams.camera` holds framing, movement and lens, and `videoParams.direction`
 * holds blocking and device (`singleClipCamera.js`). Its length is set by the composer's
 * own duration control, not per shot, so the duration chip stays excluded -- offering it
 * here would render a second control writing to a fact that already has an owner.
 *
 * EXPLICIT, not a filter, and that is the point. The old `startsWith('camera.')` filter
 * could not span two registries -- lens, blocking and device live in `ANCHOR_SLOT_CHIPS`
 * above, not in `PROMPT_SLOT_CHIPS` -- and a widened two-prefix filter would silently
 * adopt any future row onto a surface whose store must be taught each field by hand.
 * `camera.angle` and `camera.focus` already exist on the manifest type and have no store
 * here; a filter would offer them and the normalizer would drop every write. Listing the
 * paths means offering a chip and teaching the store are two visible edits, and the test
 * beside this file forces them to move together.
 */
export const SINGLE_CLIP_SLOT_CHIP_PATHS = Object.freeze([
  'camera.framing',
  'camera.movement',
  'camera.lens',
  'direction.blocking',
  'direction.device',
]);

export function getPromptSlotChipConfig(slotPath) {
  if (getCastPerformanceSlotIndex(slotPath) !== null) return PERFORMANCE_SLOT_CHIP;

  // A role-keyed acting note, titled by the role so the chip and its menu say WHOSE note
  // this is. Returning null here would render no chip at all for a live anchor, leaving a
  // resolved note as bare prose the user could not click.
  const role = getCastPerformanceSlotRole(slotPath);
  if (role !== null) return { ...PERFORMANCE_SLOT_CHIP, title: `${role} performance` };

  return PROMPT_SLOT_CHIPS[slotPath] || ANCHOR_SLOT_CHIPS[slotPath] || null;
}
